import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { applyChannelMarkupAndRoundUpToTen, NO_CHANNEL_MARKUP } from 'lib/channelMarkup/channelMarkup'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { useMemo } from 'react'

function useBedbankOfferSell(
  offer: App.BedbankOffer | App.BedbankOfferSummary,
  duration: number | undefined,
  rooms: Array<App.Occupants> | undefined,
): App.BedbankSellPriceInfo | null {
  return useMemo(() => {
    if (offer.sell?.price) {
      const numberRooms = rooms?.length || 1
      const los = duration || offer.sell.los
      // Scale the un-marked base, then mark once. `offer.sell.price` already carries the markup for
      // `sell.los` nights and one room, so scaling that instead would multiply a `fixed` flat
      // amount by the nights and the rooms — `bedbankRateMap` charges it once for the stay.
      // `basePrice` falls back to `price` for offers held in redux from before it existed, where
      // no markup applied and the two were equal.
      const unmarkedBase = offer.sell.basePrice ?? offer.sell.price
      const basePrice = Math.round((unmarkedBase / offer.sell.los) * los * numberRooms)
      const price = applyChannelMarkupAndRoundUpToTen(basePrice, offer.sell.channelMarkup ?? NO_CHANNEL_MARKUP)
      const value = Math.round((offer.sell.value / offer.sell.los) * los * numberRooms)
      const taxesAndFees = Math.round((offer.sell.taxesAndFees / offer.sell.los) * los * numberRooms)
      const propertyFees = Math.round((offer.sell.propertyFees / offer.sell.los) * los * numberRooms)
      const total = offer.sell.price + offer.sell.propertyFees
      const discountPercentage = getValueOffPercent(offer.sell.value, total)

      return {
        price,
        basePrice,
        channelMarkup: offer.sell.channelMarkup,
        value,
        propertyFees,
        taxesAndFees,
        discountPercentage: discountPercentage >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discountPercentage : 0,
        los,
      }
    }
    return null
  }, [offer, duration, rooms])
}

export default useBedbankOfferSell
